import { utcAsLocal } from '@/lib/date-format';
import { romeWallClock } from '@/lib/rome-date';

// Bookable window of an experience request, on the calendar of Rome. The Rome wall clock is
// held in a UTC Date (UTC has no DST, so day and year moves stay exact) and handed to the
// picker as a local Date with the same fields.

type Season = {
  startDay: number;
  startMonth: number;
  endDay: number;
  endMonth: number;
};

function romeNow(now: number) {
  const wall = romeWallClock(now);

  return new Date(
    Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second,
      wall.millisecond,
    ),
  );
}

const startOfYear = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), 0, 1));

const endOfYear = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 23, 59, 59, 999));

function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

const daysInMonth = (year: number, month: number) =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

// Month change keeps the day inside the new month (Jan 31 + 1 month = Feb 28)
function setMonthClamped(date: Date, month: number) {
  const result = new Date(date);
  const year = result.getUTCFullYear() + Math.floor(month / 12);
  const monthInYear = ((month % 12) + 12) % 12;
  result.setUTCFullYear(
    year,
    monthInYear,
    Math.min(result.getUTCDate(), daysInMonth(year, monthInYear)),
  );
  return result;
}

const addYear = (date: Date) => setMonthClamped(date, date.getUTCMonth() + 12);

// Season day: the month first (day kept inside it), then the day, which may roll over
function setSeasonDay(date: Date, month: number, day: number) {
  const result = setMonthClamped(date, month - 1);
  result.setUTCDate(day);
  return result;
}

function seasonIsOverThisYear(season: Season, now: number) {
  if (season.endDay > 0 && season.endMonth > 0) {
    const seasonEnd = setSeasonDay(
      endOfYear(romeNow(now)),
      season.endMonth,
      season.endDay,
    );
    return romeNow(now) > seasonEnd;
  }
  return false;
}

/** First and last day a request can be made for (local Dates for the picker). */
export function getRequestDateRange(season: Season, now: number = Date.now()) {
  // At least 3 days ahead, and not before the season starts
  const earliest = addDays(romeNow(now), 3);
  let seasonStart = startOfYear(romeNow(now));
  if (season.startDay > 0 && season.startMonth > 0) {
    if (seasonIsOverThisYear(season, now)) {
      seasonStart = startOfYear(addYear(romeNow(now)));
    }
    seasonStart = setSeasonDay(seasonStart, season.startMonth, season.startDay);
  }
  const minDate = earliest > seasonStart ? earliest : seasonStart;

  // A year ahead, or the end of the season
  let maxDate = addDays(romeNow(now), 365);
  if (season.endDay > 0 && season.endMonth > 0) {
    maxDate = endOfYear(romeNow(now));
    if (seasonIsOverThisYear(season, now)) {
      maxDate = endOfYear(addYear(romeNow(now)));
    }
    maxDate = setSeasonDay(maxDate, season.endMonth, season.endDay);
  }

  // The picker works in local time: same fields as the Rome wall clock
  return { minDate: utcAsLocal(minDate), maxDate: utcAsLocal(maxDate) };
}
